/**
 * One run's results, in the prototype's order on a single scrolling page (decision 5.3): headline
 * figures, stance by round and the opinion shift, what drove reactions, the posts, then Advanced.
 *
 * Every figure here was worked out by the API (decision 5.1). The page picks colours, draws, and
 * offers each table underneath its chart (5.4).
 */

import { FileText, FlaskConical, Info, Table2, Target, Wand2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router";
import { toast } from "sonner";

import { downloadFile, saveBlob } from "@/api/client";
import { Chart } from "@/charts/Chart";
import { phraseDrivers, stanceByRound } from "@/charts/options";
import { TableView } from "@/charts/TableView";
import { percent } from "@/charts/theme";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Commitments } from "@/app/Commitments";
import { GoalSeekPanel } from "@/app/GoalSeek";
import { WhatIfPanel } from "@/app/WhatIf";
import { type Dashboard, type QuickLook, type ThemeSet, useDashboard, useMe, useRun, useStudy } from "@/lib/queries";

import { Help } from "@/components/Help";
import { PublishExample } from "@/app/PublishExample";
import { useNarrated, useTour } from "@/app/tour";

import { intervalHelp, PANEL_HELP, TILE_HELP } from "./ResultHelp";
import { Advanced } from "./ResultAdvanced";

type ValidationRow = Dashboard["validation"][number];

/**
 * The page in five parts (decision 8.15, superseding 5.3). Ordered as the single scrolling page
 * was, so anybody who knew where things were still does.
 *
 * `results` is the default and is left out of the URL, so the plain run link keeps working and
 * opens where it always did.
 */
const TABS = [
  { value: "results", label: "Results" },
  { value: "reactions", label: "Reactions" },
  { value: "themes", label: "Themes" },
  { value: "posts", label: "Posts" },
  { value: "advanced", label: "Advanced" },
] as const;

const DEFAULT_TAB = TABS[0].value;

export function ResultPage() {
  const { studyId, runId } = useParams();
  const study = useStudy(studyId).data;
  const dashboard = useDashboard(studyId, runId);
  const run = useRun(studyId, runId);
  const me = useMe().data;
  const view = dashboard.data;
  const [whatIf, setWhatIf] = useState(false);
  const [goalSeek, setGoalSeek] = useState(false);
  // In the URL, so a refresh keeps your place and a link can open on the part being discussed.
  // An unknown value falls back rather than showing nothing, since these links get edited by hand.
  const [params, setParams] = useSearchParams();
  const asked = params.get("view") ?? "";
  const tab = TABS.some((t) => t.value === asked) ? asked : DEFAULT_TAB;
  const showTab = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === DEFAULT_TAB) next.delete("view");
    else next.set("view", value);
    setParams(next);
  };
  // A quick look's figures, drawn beside the result and never over it (decision 6.10).
  const [quickLook, setQuickLook] = useState<QuickLook | null>(null);
  // Where the button sits when it is pressed, so the panel opens level with it rather than
  // centred - the eye should not have to travel away from the sentence that raised the question.
  const [whyFlagged, setWhyFlagged] = useState<number | null>(null);
  // Built once per set of rows, above the early returns: hooks must run in the same order every time.
  // The rail offers Play for whatever result is on screen, so the page has to say what that is.
  useNarrated(studyId, runId, Boolean(view));
  const stanceOption = useMemo(
    () => (view ? stanceByRound(view.stance_by_round, quickLook?.metrics.category_counts ? quickLook : null) : null),
    [view, quickLook],
  );
  const driversOption = useMemo(() => (view ? phraseDrivers(view.drivers) : null), [view]);

  if (dashboard.isLoading) return <Loading />;
  if (!view)
    return (
      <section className="mx-auto max-w-4xl px-6 py-10">
        <Alert variant="destructive">
          <AlertDescription>
            {dashboard.error instanceof Error ? dashboard.error.message : "This run has no results."}
          </AlertDescription>
        </Alert>
      </section>
    );

  return (
    <section className="mx-auto max-w-5xl space-y-8 bg-[rgb(15_17_21/0.88)] px-6 py-10 backdrop-blur-sm">
      <header className="space-y-1">
        <p className="text-sm text-muted-foreground">
          {study?.name}
          {view.mode === "mock" && " · mock, synthetic reactions"}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold">{view.variant_label}</h1>
          <div className="flex gap-2">
            {/* Its indicative figures are drawn on the tiles and Stance by round, so go there
                first - a quick look that changes something off-screen has not answered anything. */}
            <Button variant="outline" size="sm"
                    onClick={() => { showTab(DEFAULT_TAB); setWhatIf(true); }} aria-expanded={whatIf}>
              <Wand2 aria-hidden="true" /> What if…
            </Button>
            <Button variant="outline" size="sm" onClick={() => setGoalSeek(true)} aria-expanded={goalSeek}>
              <Target aria-hidden="true" /> Goal seek…
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Based on <span className="text-foreground">{view.n} simulated personas</span>, so one persona moves a share
          by about {view.points_per_persona.toFixed(0)} percentage points. Final-round stances.
        </p>
      </header>

      {view.reliability.flagged > 0 && (
        <Alert>
          <FlaskConical aria-hidden="true" />
          <AlertDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              {view.reliability.flagged} of {view.reliability.personas} personas were flagged as unreliable in
              validation{view.reliability.excluded ? " and excluded from this simulation." : " but were still simulated."}
            </span>
            {/* The detail has always been on this page, forty rows down inside a collapsed section.
                This is the sentence it belongs to, so this is where the way in belongs. */}
            <Button variant="ghost" size="xs"
                    onClick={(event) => setWhyFlagged(event.currentTarget.getBoundingClientRect().top)}>
              More info
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {whyFlagged !== null && (
        <WhyFlagged view={view} anchorTop={whyFlagged} onClose={() => setWhyFlagged(null)} />
      )}

      {view.divided && (
        <p className="rounded-lg border border-step-next/40 bg-step-next/10 p-3 text-sm text-step-next">
          <b>Divided audience:</b> at least a quarter of personas are positive and at least a quarter negative. The
          average hides a split.
        </p>
      )}

      <Tabs value={tab} onValueChange={(value) => showTab(String(value))}>
        <TabsList aria-label="Result sections">
          {TABS.map((section) => (
            <TabsTrigger key={section.value} value={section.value}>{section.label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="results">
          <Tiles view={view} quickLook={quickLook} />

          <div className="grid gap-6 md:grid-cols-5">
            <Panel title="Stance by round" help={PANEL_HELP["Stance by round"]} narrates="stance_by_round"
                   className="md:col-span-3">
              <Chart
                option={stanceOption!}
                height={Math.max(150, 60 * view.rounds)}
                description={`Share of personas at each stance, for ${view.rounds} round${view.rounds === 1 ? "" : "s"}`}
              />
              <TableView
                title="Stance by round"
                rows={view.stance_by_round.map((r) => ({
                  Round: r.round_label,
                  Stance: r.stance_label,
                  Personas: r.personas,
                  Share: percent(r.share),
                }))}
              />
            </Panel>

            <Panel title="Opinion shift" help={PANEL_HELP["Opinion shift"]} narrates="opinion_shift"
                   className="md:col-span-2">
              {view.shift === null || view.rounds < 2 ? (
                <p className="text-sm text-muted-foreground">Only one round was run, so there is no shift to show.</p>
              ) : (
                <>
                  <p className="text-3xl font-semibold tabular-nums">
                    {view.shift > 0 ? "+" : ""}
                    {view.shift.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Mean stance change, round 1 → final</p>
                  <p className="mt-3 text-sm">
                    {view.moved_up} personas moved up and {view.moved_down} moved down after seeing each other's posts.
                  </p>
                </>
              )}
              <p className="mt-4 text-sm text-muted-foreground">
                Positive <span className="text-foreground">{view.category_counts.positive ?? 0}</span> · Neutral{" "}
                <span className="text-foreground">{view.category_counts.neutral ?? 0}</span> · Negative{" "}
                <span className="text-foreground">{view.category_counts.negative ?? 0}</span>
              </p>
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="reactions">
          <Panel title="What drove reactions" help={PANEL_HELP["What drove reactions"]}>
            <p className="mb-2 text-xs text-muted-foreground">
              Scenario sentences that personas quoted as shaping their reaction, each persona counted once per sentence.
            </p>
            {view.drivers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No personas quoted the scenario.</p>
            ) : (
              <>
                <Chart
                  option={driversOption!}
                  height={Math.max(160, 42 * view.drivers.length)}
                  description="Scenario sentences personas quoted, split by whether those personas ended up positive, neutral or negative"
                />
                <TableView
                  title="Drivers"
                  rows={view.drivers.map((d) => ({
                    "Scenario sentence": d.sentence,
                    Positive: d.positive,
                    Neutral: d.neutral,
                    Negative: d.negative,
                    Total: d.total,
                  }))}
                />
              </>
            )}
            {view.quotes_unmatched > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {view.quotes_unmatched} of {view.quotes_total} quotes were not verbatim scenario text and are not counted.
              </p>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="themes">
          <Panel title="Themes" help={PANEL_HELP["Themes"]}>
            <p className="mb-2 text-xs text-muted-foreground">
              Reasons grouped into themes, one model call per run - check the evidence.
            </p>
            <Themes themes={run.data?.themes ?? null} error={run.data?.themes_error ?? null} names={view.persona_names}
                    studyId={studyId!} runId={view.run_id} />
          </Panel>
        </TabsContent>

        <TabsContent value="posts">
          <Posts view={view} />
        </TabsContent>

        <TabsContent value="advanced">
          <Advanced view={view} />
        </TabsContent>
      </Tabs>

      {whatIf && study && (
        <WhatIfPanel study={study} view={view} onClose={() => setWhatIf(false)}
                     onQuickLook={(look) => { showTab(DEFAULT_TAB); setQuickLook(look); }} />
      )}
      {goalSeek && studyId && (
        <GoalSeekPanel studyId={studyId} runId={view.run_id} onClose={() => setGoalSeek(false)} />
      )}

      <footer className="flex flex-wrap items-center gap-2 border-t border-white/8 pt-4">
        <Button
          variant="outline"
          onClick={() =>
            void downloadFile(`/api/v1/studies/${studyId}/runs/${runId}/workbook.xlsx`).catch((err: unknown) =>
              toast.error(err instanceof Error ? err.message : "the download failed"),
            )
          }
        >
          <Table2 aria-hidden="true" /> Download everything (Excel)
        </Button>
        {run.data?.report_md && (
          <Button
            variant="ghost"
            onClick={() =>
              saveBlob(new Blob([run.data!.report_md!], { type: "text/markdown;charset=utf-8" }), `${runId}-report.md`)
            }
          >
            <FileText aria-hidden="true" /> Full report (Markdown)
          </Button>
        )}
        {/* An offer, not a gate: the endpoint checks for itself, as the admin link in the rail does. */}
        {me?.is_admin && studyId && runId && (
          <PublishExample
            studyId={studyId}
            runId={runId}
            suggestedTitle={study?.name ?? "Example"}
            suggestedFigure={`${view.tiles[0]?.formatted ?? ""} positive`}
          />
        )}
        {view.usage_totals.calls > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {view.usage_totals.calls} model calls · {(view.usage_totals.input_tokens ?? 0).toLocaleString()} input and{" "}
            {(view.usage_totals.output_tokens ?? 0).toLocaleString()} output tokens
          </span>
        )}
      </footer>
    </section>
  );
}

function Tiles({ view, quickLook }: { view: Dashboard; quickLook: QuickLook | null }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {view.tiles.map((tile) => (
        <Narrated key={tile.key} narrates={tile.key} className="rounded-xl border bg-[#16181d] p-3">
          <p className="text-xs text-muted-foreground">
            {tile.label}
            {TILE_HELP[tile.key] && (
              <Help about={tile.label} text={TILE_HELP[tile.key]} note={intervalHelp(view.n)} />
            )}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{tile.formatted}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{tile.interval}</p>
          {quickLook && tile.key in quickLook.metrics.values && (
            <p className="mt-1 border-t border-dashed border-step-next/40 pt-1 text-[11px] text-step-next">
              what if {formatMetric(tile.key, quickLook.metrics.values[tile.key])}{" "}
              <span className="text-muted-foreground">(indicative)</span>
            </p>
          )}
        </Narrated>
      ))}
    </div>
  );
}

const PERCENT = new Set(["positive_share", "negative_share", "backlash_share"]);

function formatMetric(key: string, value: number): string {
  return PERCENT.has(key) ? `${Math.round(value * 100)}%` : value.toFixed(2);
}

export function Panel({ title, help, narrates, children, className }: {
  title: string;
  /** The tour's name for this panel, if the tour talks about it. */
  narrates?: string;
  /** What this panel is showing, for a reader meeting it for the first time. Optional: the panels
      outside the Result page explain themselves in the words already on them. */
  help?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      <h2 className="mb-2 text-sm font-medium tracking-wide text-muted-foreground uppercase">
        {title}
        {help && <Help about={title.toLowerCase()} text={help} />}
      </h2>
      <Narrated narrates={narrates} className="rounded-xl border bg-[#16181d] p-4">
        {children}
      </Narrated>
    </section>
  );
}


/**
 * A panel the narrated tour can point at.
 *
 * While it is being spoken about it takes the tour's green border - and a marker beside it, because
 * a green outline round "Backlash 40%" would otherwise read as the app approving the number, and
 * colour alone tells a colour-blind reader nothing. It also scrolls itself into view: a voice
 * describing a panel that is off-screen has answered nothing.
 */
export function Narrated({ narrates, className, children }: {
  narrates?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { activeKey } = useTour();
  const live = Boolean(narrates) && activeKey === narrates;
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (live) box.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [live]);

  return (
    <div
      ref={box}
      data-narrates={narrates}
      data-live={live ? "" : undefined}
      className={`${className ?? ""} relative transition-colors ${
        live ? "border-[#0ca30c]" : "border-white/10"
      }`}
    >
      {live && (
        <span
          aria-hidden="true"
          className="absolute -top-2 -left-2 flex size-5 items-center justify-center rounded-full bg-[#0ca30c] text-[10px] text-black"
        >
          ▶
        </span>
      )}
      {children}
    </div>
  );
}

function Themes({
  themes,
  error,
  names,
  studyId,
  runId,
}: {
  themes: ThemeSet | null;
  error: string | null;
  names: Record<string, string>;
  studyId: string;
  runId: string;
}) {
  const found = themes?.themes ?? [];
  if (!themes) return <p className="text-sm text-muted-foreground">Themes unavailable: {error ?? "not generated"}</p>;
  return (
    <div className="space-y-3">
      {(["concern", "appeal"] as const).map((polarity) => {
        const items = found.filter((t) => t.polarity === polarity);
        if (!items.length) return null;
        return (
          <div key={polarity} className="space-y-1.5">
            <h3 className="text-sm font-medium">{polarity === "concern" ? "Concerns" : "Appeals"}</h3>
            {items.map((theme) => (
              <details key={theme.label} className="rounded-lg border border-white/10 bg-black/20 p-2">
                <summary className="cursor-pointer text-sm">
                  <span className={polarity === "concern" ? "text-[#ff8f75]" : "text-[#a5d2ff]"} aria-hidden="true">
                    {polarity === "concern" ? "▼" : "▲"}
                  </span>{" "}
                  {theme.label} · {theme.persona_ids.length} personas
                </summary>
                <p className="mt-2 text-sm">{theme.description}</p>
                {(theme.evidence ?? []).map((quote) => (
                  <blockquote key={quote} className="mt-2 border-l-2 border-white/15 pl-2 text-sm text-muted-foreground">
                    {quote}
                  </blockquote>
                ))}
                <p className="mt-2 text-xs text-muted-foreground">
                  Personas: {theme.persona_ids.map((id) => `${id} (${names[id] ?? "?"})`).join(", ")}
                </p>
                {polarity === "concern" && (
                  <Commitments studyId={studyId} runId={runId} themeId={theme.theme_id} />
                )}
              </details>
            ))}
          </div>
        );
      })}
    </div>
  );
}

const FILTERS = ["All", "Positive", "Neutral", "Negative"] as const;

function Posts({ view }: { view: Dashboard }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("All");
  const shown = view.posts.filter((p) => filter === "All" || p.category === filter.toLowerCase());
  return (
    <Panel title="Posts" help={PANEL_HELP["Posts"]}>
      <div className="mb-3 flex flex-wrap gap-1">
        {FILTERS.map((option) => (
          <Button
            key={option}
            size="xs"
            variant={filter === option ? "default" : "ghost"}
            aria-pressed={filter === option}
            onClick={() => setFilter(option)}
          >
            {option}
          </Button>
        ))}
      </div>
      <div className="max-h-[32rem] space-y-2 overflow-auto pr-1">
        {shown.map((post) => (
          <article key={post.persona_id} className="rounded-lg border border-white/10 bg-black/20 p-3">
            <p className="text-xs text-muted-foreground">
              {post.persona_id} · {post.name} — <span className="text-foreground">{post.stance_label}</span>
            </p>
            <p className="mt-1 text-sm">{post.post}</p>
            {post.reasons.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">{post.reasons.join("; ")}</p>
            )}
          </article>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground">No posts in this group.</p>}
      </div>
      <TableView
        title="Posts"
        rows={view.posts.map((p) => ({
          Persona: `${p.persona_id} · ${p.name}`,
          Stance: p.stance_label,
          Post: p.post,
          Reasons: p.reasons.join("; "),
          Quoted: p.quoted.join(" | "),
        }))}
      />
      {view.most_disagreed.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-muted-foreground">
            Most disagreed-with posts (backlash signals)
          </summary>
          <div className="mt-2 space-y-2">
            {view.most_disagreed.map((post) => (
              <p key={`${post.persona_id}-${post.round}`} className="text-sm">
                <b>
                  {post.persona_id} · {post.name}
                </b>{" "}
                <span className="text-xs text-muted-foreground">
                  (round {post.round}, {post.disagreements} disagreements)
                </span>
                : {post.post}
              </p>
            ))}
          </div>
        </details>
      )}
    </Panel>
  );
}

function Loading() {
  return (
    <section className="mx-auto max-w-5xl space-y-6 px-6 py-10">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64" />
      <span className="sr-only">Loading results…</span>
    </section>
  );
}

/**
 * Why those personas were flagged.
 *
 * Every figure here was already on the page - `Dashboard.validation` carries a row per persona, and
 * the Advanced section renders the whole table. What was missing was a way in from the sentence
 * that raises the question, so this shows the same rows at the point they are asked about.
 *
 * The wording is taken from the Markdown report rather than written fresh, so the screen and the
 * download cannot end up saying different things about what these numbers mean.
 */
function WhyFlagged({
  view,
  anchorTop,
  onClose,
}: {
  view: Dashboard;
  anchorTop: number;
  onClose: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  // Which row's explanation to show, and whether the mouse leaving should take it away again.
  // Hovering is for glancing down the column; clicking is for reading, and for selecting the text.
  const [detail, setDetail] = useState<{ row: ValidationRow; at: DOMRect; pinned: boolean } | null>(null);
  const { reliability, validation } = view;
  const rows = showAll ? validation : validation.filter((row) => row.flagged);
  const number = (value: number | null) => (value === null ? "–" : value.toFixed(2));

  const show = (row: ValidationRow, target: HTMLElement, pinned: boolean) => {
    if (detail?.pinned && !pinned) return; // a pinned card is not displaced by a passing mouse
    setDetail({ row, at: target.getBoundingClientRect(), pinned });
  };

  // Level with the button, but never so low that the panel has no room: on a short window it
  // slides up far enough to show something worth reading.
  const margin = 16;
  const room = Math.max(240, Math.min(560, window.innerHeight - anchorTop - margin));
  const top = Math.max(margin, Math.min(anchorTop, window.innerHeight - room - margin));

  return (
    <div className="fixed inset-0 z-50 bg-black/60" onClick={onClose}>
      <div
        className="absolute left-1/2 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-[#16181d] p-4"
        style={{ top, maxHeight: `calc(100vh - ${top + margin}px)` }}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="font-medium">
          Why {reliability.flagged} of {reliability.personas} personas were flagged
        </h2>

        <p className="text-xs text-muted-foreground">
          Each persona answered two parallel 15-item questionnaires in separate, independent calls. Three things
          were compared, and a persona is flagged if any falls short: how consistently it answered within one
          form (below {reliability.alpha_threshold}), how closely its two forms agreed (retest r below{" "}
          {reliability.retest_threshold})
          {reliability.fidelity_threshold !== null
            ? `, and how well the answers matched the personality it was given (fidelity r below ${reliability.fidelity_threshold})`
            : ""}
          . A measure that cannot be calculated — answering identically to every item, for instance — counts as a
          flag and shows as “–”.
        </p>

        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1115] text-left text-xs uppercase text-muted-foreground">
              <tr>
                {["Persona", "Details", "Consistency", "Retest r", "Fidelity r", "Why"].map((head) => (
                  <th key={head} className="px-3 py-2 font-medium">{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.persona_id} className="border-t border-white/5">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.name || row.persona_id}
                    {showAll && row.flagged && <span className="ml-1 text-step-todo">•</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`What the figures mean for ${row.name || row.persona_id}`}
                      aria-expanded={detail?.row.persona_id === row.persona_id}
                      onMouseEnter={(event) => show(row, event.currentTarget, false)}
                      onMouseLeave={() => setDetail((open) => (open?.pinned ? open : null))}
                      onFocus={(event) => show(row, event.currentTarget, false)}
                      onBlur={() => setDetail((open) => (open?.pinned ? open : null))}
                      onClick={(event) => show(row, event.currentTarget, true)}
                    >
                      <Info aria-hidden="true" />
                    </Button>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{number(row.internal_consistency)}</td>
                  <td className="px-3 py-2 tabular-nums">{number(row.test_retest_r)}</td>
                  <td className="px-3 py-2 tabular-nums">{number(row.trait_fidelity_r)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{row.reasons.join("; ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          These describe the consistency of model role-play, not human psychology: personas are not people, and
          the within-person figures are built on five observations, so they are noisy and not comparable to
          published values. The same table, every persona's explanation included, is in the Excel workbook.
        </p>

        <div className="flex justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
            {showAll ? `Show only the ${reliability.flagged} flagged` : `Show all ${reliability.personas}`}
          </Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>

      {detail && (
        <Explanation
          row={detail.row}
          at={detail.at}
          pinned={detail.pinned}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}


/**
 * One persona's figures said in English, beside the button that asked for them.
 *
 * Fixed rather than positioned inside the table, because the table scrolls in both directions and a
 * card clipped by its own container would be worse than no card. While it is only hovered it cannot
 * be clicked through to - the mouse must be free to keep moving down the column - and once pinned it
 * gets its own backdrop, so clicking away returns to the table rather than closing the panel behind.
 *
 * Through a portal to `document.body`, because this page's section carries `backdrop-blur`, and an
 * element with a backdrop filter is the containing block for the fixed-position elements inside it.
 * Left where it was written, the card would be placed against that section rather than the window -
 * and the backdrop would stop short of the sidebar, so a click there would dismiss nothing. React
 * events still travel to this component's parent from a portal, so the panel behind stays put.
 */
function Explanation({
  row,
  at,
  pinned,
  onClose,
}: {
  row: ValidationRow;
  at: DOMRect;
  pinned: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!pinned) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [pinned, onClose]);

  const margin = 16;
  const width = Math.min(420, window.innerWidth - 2 * margin);
  const left = Math.max(margin, Math.min(at.left - 8, window.innerWidth - width - margin));
  // Below the button, unless the bottom of the window is closer than the card is likely to be tall.
  const below = window.innerHeight - at.bottom > 220;
  const place = below
    ? { top: at.bottom + 8, maxHeight: window.innerHeight - at.bottom - 8 - margin }
    : { bottom: window.innerHeight - at.top + 8, maxHeight: at.top - 8 - margin };

  const card = (
    <div
      role={pinned ? "dialog" : "tooltip"}
      aria-label={`What the figures mean for ${row.name || row.persona_id}`}
      className={`fixed z-[60] space-y-2 overflow-y-auto rounded-xl border border-white/15 bg-[#1b1e24] p-3 text-sm shadow-xl ${
        pinned ? "" : "pointer-events-none"
      }`}
      style={{ left, width, ...place }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">{row.details.headline}</p>
        {pinned && (
          <Button variant="ghost" size="icon-xs" aria-label="Close" onClick={onClose}>
            <X aria-hidden="true" />
          </Button>
        )}
      </div>

      {row.details.findings.length > 0 && (
        <ul className="space-y-1.5 text-muted-foreground">
          {row.details.findings.map((finding) => (
            <li key={finding}>{finding}</li>
          ))}
        </ul>
      )}

      {row.details.passed.length > 0 && (
        <div className="space-y-1 border-t border-white/10 pt-2 text-muted-foreground">
          <p className="text-xs uppercase tracking-wide">What passed</p>
          {row.details.passed.map((good) => (
            <p key={good}>{good}</p>
          ))}
        </div>
      )}

      {!pinned && <p className="text-xs text-muted-foreground/70">Click to keep this open.</p>}
    </div>
  );

  // Its own backdrop once pinned: a click anywhere puts the table back, and leaves the panel alone.
  return createPortal(
    pinned ? (
      <div className="fixed inset-0 z-[55]" onClick={(event) => { event.stopPropagation(); onClose(); }}>
        {card}
      </div>
    ) : (
      card
    ),
    document.body,
  );
}
